import React, { useEffect, useRef, useState } from "react";
import "./deskKit.css";

/**
 * Desktop data table (≥1024px composition; render it only on desktop).
 *
 * columns: [{ key, label, align?: "right"|"center", width?, className?,
 *             render(row) => node, primary?: boolean }]
 *   `primary` marks the cell that carries the open control. The whole row is
 *   clickable through that control (stretched ::after), so keyboard / screen
 *   readers get one button per row and nested controls stay independent.
 * groups: [{ key, header: node, rows }] — optional grouped sections (date, employee…)
 * onOpen(row) / openLabel(row): row navigation.
 * rowClassName(row): extra row state classes (is-muted, is-selected, …).
 */
export function DeskTable({
  columns,
  rows,
  groups,
  rowKey,
  onOpen,
  openLabel,
  rowClassName,
  caption,
  className = "",
  testId,
}) {
  const sections = groups || [{ key: "all", header: null, rows: rows || [] }];
  const span = columns.length;

  const renderRow = (row) => (
    <tr
      key={rowKey(row)}
      className={["dt-row", onOpen ? "is-openable" : "", rowClassName ? rowClassName(row) : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {columns.map((col) => {
        const content = col.render(row);
        const isPrimary = col.primary && onOpen;
        return (
          <td
            key={col.key}
            className={["dt-cell", col.align ? `dt-cell--${col.align}` : "", col.className || ""]
              .filter(Boolean)
              .join(" ")}
          >
            {isPrimary ? (
              <button
                type="button"
                className="dt-open"
                onClick={() => onOpen(row)}
                aria-label={openLabel ? openLabel(row) : undefined}
              >
                {content}
              </button>
            ) : (
              content
            )}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className={`dt-card ${className}`.trim()} data-testid={testId}>
      <table className="dt">
        {caption ? <caption className="dt-sr">{caption}</caption> : null}
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={col.width ? { width: col.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={["dt-th", col.align ? `dt-cell--${col.align}` : ""].filter(Boolean).join(" ")}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        {sections.map((section) => (
          <tbody key={section.key}>
            {section.header ? (
              <tr className="dt-group">
                <td colSpan={span}>{section.header}</td>
              </tr>
            ) : null}
            {section.rows.map(renderRow)}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/** ⋮ actions menu that sits above the stretched row-open control. */
export function DeskRowMenu({ items, label = "Actions", onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown") {
        if (event.key === "Escape") setOpen(false);
        return;
      }
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  if (!items || items.length === 0) return null;
  return (
    <div className="dt-menuwrap" ref={ref}>
      <button
        type="button"
        className="dt-kebab"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open ? (
        <div className="dt-menu" role="menu">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={item.danger ? "is-danger" : ""}
              onClick={() => {
                setOpen(false);
                (onSelect || item.onSelect)?.(item);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Toolbar: tabs / search / filters / primary action in one desktop row. */
export function DeskToolbar({ children, className = "" }) {
  return <div className={`dt-toolbar ${className}`.trim()}>{children}</div>;
}

export function DeskTabs({ options, value, onChange, ariaLabel }) {
  if (!options || options.length < 2) return null;
  return (
    <div className="dt-tabs" role="tablist" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={`dt-tab${value === option.value ? " is-on" : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count != null ? <span className="dt-tab__count">{option.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function DeskSearch({ value, onChange, placeholder, ariaLabel, width }) {
  return (
    <label className="dt-search" style={width ? { width } : undefined}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        autoComplete="off"
      />
    </label>
  );
}

export function DeskPill({ tone = "muted", children }) {
  return <span className={`dt-pill dt-pill--${tone}`}>{children}</span>;
}

/** Bordered desktop panel with an optional title row. */
export function DeskPanel({ title, actions, children, className = "" }) {
  return (
    <section className={`dp ${className}`.trim()}>
      {title || actions ? (
        <header className="dp__head">
          {title ? <h2 className="dp__title">{title}</h2> : <span />}
          {actions ? <div className="dp__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="dp__body">{children}</div>
    </section>
  );
}

export function DeskEmpty({ title, children }) {
  return (
    <div className="dt-empty">
      {title ? <strong>{title}</strong> : null}
      {children ? <span>{children}</span> : null}
    </div>
  );
}
